import { useEffect, useMemo, useRef, useState } from "react";
import { HTTP_HEADERS, REQUEST_CREDENTIALS } from "@siisel/frontend-shared";
import { ODATA_LOOKUP_DEBOUNCE_MS, ODATA_LOOKUP_PAGE_SIZE, ODATA_NAME_FIELD } from "../config/constants";
import type { LegacyFormField, LegacyFormFieldValue, LegacyLookupOption } from "../types";
import { toStringArray } from "../utils/legacyForm";

type Props = {
  disabled: boolean;
  field: LegacyFormField;
  inputId: string;
  lookupUrl?: string;
  onChange: (value: string[]) => void;
  value: LegacyFormFieldValue;
};

function normalizeLookupOption(option: LegacyLookupOption): LegacyLookupOption {
  return { value: String(option.value), text: String(option.text) };
}

function getOdataItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    return Array.isArray(record.value) ? record.value : Array.isArray(record.Value) ? record.Value : [];
  }

  return [];
}

function readOdataOption(item: unknown): LegacyLookupOption | null {
  if (!item || typeof item !== "object") {
    return null;
  }

  const record = item as Record<string, unknown>;
  const value = record.id ?? record.Id ?? record.ID;
  const text = record.name ?? record.Name ?? record.text ?? record.Text;
  return value == null || text == null ? null : { value: String(value), text: String(text) };
}

function escapeOdataString(value: string): string {
  return value.replace(/'/g, "''");
}

export function OdataMultiSelect({ disabled, field, inputId, lookupUrl, onChange, value }: Props) {
  const currentValues = useMemo(() => toStringArray(value), [value]);
  const initialSelectedOptions = useMemo(
    () => (field.selectedOptions ?? field.options ?? []).map(normalizeLookupOption),
    [field.options, field.selectedOptions]
  );
  const [selectedOptions, setSelectedOptions] = useState<LegacyLookupOption[]>(initialSelectedOptions);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LegacyLookupOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelectedOptions((current) => {
      const optionsByValue = new Map(current.map((option) => [option.value, option]));
      initialSelectedOptions.forEach((option) => optionsByValue.set(option.value, option));
      return currentValues.map((currentValue) => optionsByValue.get(currentValue) ?? { value: currentValue, text: currentValue });
    });
  }, [currentValues, initialSelectedOptions]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  useEffect(() => {
    if (!isOpen || disabled || !lookupUrl) {
      return;
    }

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const url = new URL(lookupUrl, window.location.origin);
        url.searchParams.set("$top", String(ODATA_LOOKUP_PAGE_SIZE));
        url.searchParams.set("$orderby", ODATA_NAME_FIELD);
        if (query.trim()) {
          url.searchParams.set(
            "$filter",
            `contains(tolower(${ODATA_NAME_FIELD}),'${escapeOdataString(query.trim().toLowerCase())}')`
          );
        }

        const response = await fetch(url, {
          credentials: REQUEST_CREDENTIALS,
          headers: HTTP_HEADERS.json,
          signal: abortController.signal
        });
        if (!response.ok) {
          throw new Error(`OData lookup failed: ${response.status}`);
        }

        const options = getOdataItems(await response.json())
          .map(readOdataOption)
          .filter((option): option is LegacyLookupOption => Boolean(option));
        const selectedValues = new Set(currentValues);
        setResults(options.filter((option) => !selectedValues.has(option.value)));
      } catch {
        if (!abortController.signal.aborted) {
          setResults([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, ODATA_LOOKUP_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [currentValues, disabled, isOpen, lookupUrl, query]);

  const selectOption = (option: LegacyLookupOption) => {
    const nextOptions = [...selectedOptions.filter((selected) => selected.value !== option.value), option];
    setSelectedOptions(nextOptions);
    onChange(nextOptions.map((selected) => selected.value));
    setQuery("");
    setIsOpen(false);
  };

  const removeOption = (valueToRemove: string) => {
    const nextOptions = selectedOptions.filter((option) => option.value !== valueToRemove);
    setSelectedOptions(nextOptions);
    onChange(nextOptions.map((option) => option.value));
  };

  return (
    <div className="forms-app-odata-multiselect" ref={wrapperRef}>
      {currentValues.map((currentValue) => (
        <input type="hidden" name={field.name} value={currentValue} key={`${field.name}-${currentValue}`} />
      ))}
      <div className={`forms-app-odata-multiselect__control ${disabled ? "is-disabled" : ""}`} onClick={() => !disabled && setIsOpen(true)}>
        {selectedOptions.map((option) => (
          <span className="forms-app-odata-multiselect__choice" key={option.value}>
            <span>{option.text}</span>
            {!disabled ? (
              <button type="button" aria-label={`Odebrat ${option.text}`} onClick={(event) => { event.stopPropagation(); removeOption(option.value); }}>
                x
              </button>
            ) : null}
          </span>
        ))}
        {!disabled ? (
          <input
            id={inputId}
            value={query}
            placeholder={selectedOptions.length ? "" : field.placeholder}
            onChange={(event) => { setQuery(event.currentTarget.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
          />
        ) : null}
      </div>
      {isOpen && !disabled ? (
        <div className="forms-app-odata-multiselect__results">
          {isLoading ? <div className="forms-app-odata-multiselect__empty">Načítám...</div> : null}
          {!isLoading && results.length === 0 ? <div className="forms-app-odata-multiselect__empty">Žádné výsledky</div> : null}
          {!isLoading ? results.map((option) => (
            <button type="button" className="forms-app-odata-multiselect__option" key={option.value} onClick={() => selectOption(option)}>
              {option.text}
            </button>
          )) : null}
        </div>
      ) : null}
    </div>
  );
}
